import { Button, Card, Input, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type TitleRow = {
  id: string;
  code: string;
  title: string;
  author?: string | null;
  isbn?: string | null;
  is_active?: number | boolean;
  copy_count?: number;
  available_count?: number;
};

export default function LibraryTitlesPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.library.manage') || user?.permissions?.includes('education.admin')
  );
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-library-titles', q],
    queryFn: async () => {
      const res = await api.get<TitleRow[]>('/education/library/titles', { params: { q: q || undefined } });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('libraryTitles')}
      description={t('libraryTitlesIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/library/titles/new')}>
              {t('newLibraryTitle')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/library')}>{t('backToLibrary')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search')}
              allowClear
              style={{ width: 260 }}
            />
            <Button onClick={() => list.refetch()} loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Space>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              { title: t('code'), dataIndex: 'code', width: 120 },
              { title: t('title'), dataIndex: 'title' },
              { title: t('author'), dataIndex: 'author', render: (v) => v || '—' },
              { title: t('isbn'), dataIndex: 'isbn', width: 140, render: (v) => v || '—' },
              { title: t('copies'), dataIndex: 'copy_count', width: 80 },
              { title: t('available'), dataIndex: 'available_count', width: 90 },
              {
                title: t('status'),
                dataIndex: 'is_active',
                width: 90,
                render: (v) =>
                  v === 0 || v === false ? <Tag>{t('inactive')}</Tag> : <Tag color="success">{t('active')}</Tag>,
              },
              {
                title: t('actions'),
                width: canManage ? 220 : 120,
                render: (_: unknown, row: TitleRow) => (
                  <Space size="small" wrap>
                    <Button size="small" onClick={() => navigate(`/education/library/titles/${row.id}/copies`)}>
                      {t('viewCopies')}
                    </Button>
                    {canManage ? (
                      <Button size="small" onClick={() => navigate(`/education/library/titles/${row.id}/edit`)}>
                        {t('edit')}
                      </Button>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
